import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import { Tool, type ToolConstructorArgs } from './Tool';

import type { AnySkill } from 'js/src/agent/skills/types';
import type { ToolExecutionContext, ToolResult } from 'js/src/agent/types';
import { announceCreationStage, CreationStage } from 'js/src/data/agent-activity';

export const loadSkillSchema = z.object({
    name: z.string().describe('The exact name of the skill to load.'),
});
export type LoadSkillArgs = z.infer<typeof loadSkillSchema>;

interface LoadSkillToolConstructorArgs extends ToolConstructorArgs {
    /**
     * The exact skills the agent offers, already surface- and flag-filtered.
     * The tool resolves and lists names from this list only, so the loadable
     * skills match the ones described in the system prompt.
     */
    skills: AnySkill[];
    /**
     * Whether a video already exists. Loading a video skill is the earliest
     * signal that a from-scratch build has begun, but only when there is nothing
     * to build onto — otherwise it's a follow-on edit, which gets no stage panel.
     */
    getCurrentVideoId?: () => number | null;
}

const ACRONYMS: Record<string, string> = {
    ai: 'AI',
    vo: 'VO',
};

/** Turn a kebab-case skill slug into a human-friendly Title Case name. */
export function formatSkillName(slug: string): string {
    return slug
        .split('-')
        .filter(Boolean)
        .map((word) => ACRONYMS[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export class LoadSkillTool extends Tool<typeof loadSkillSchema, LoadSkillToolConstructorArgs> {
    protected override readonly schema = loadSkillSchema;
    protected override readonly toolName = TOOL_NAMES.loadSkill;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Load the full instructions for one of the available recipes or skills by name. Call this when an entry listed under "Available Recipes & Skills" is relevant to the user request, before carrying out the task.';

    private skills: AnySkill[];
    private getCurrentVideoId?: () => number | null;

    public constructor(args: LoadSkillToolConstructorArgs) {
        super(args);
        this.skills = args.skills;
        this.getCurrentVideoId = args.getCurrentVideoId;
    }

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<LoadSkillArgs>;
                if (parsed.name) {
                    const kind = this.skills.find((s) => s.name === parsed.name)?.kind;
                    const noun = kind && kind !== 'howto' ? 'recipe' : 'skill';
                    return `Using ${noun}: ${formatSkillName(parsed.name)}`;
                }
            } catch {
                // fall through to default name
            }
        }
        return 'Using skill';
    }

    public get statusIcon(): string {
        return 'school';
    }

    public async execute(args: LoadSkillArgs, context: ToolExecutionContext): Promise<ToolResult> {
        const skill = this.skills.find((s) => s.name === args.name);
        if (!skill) {
            const available = this.skills.map((s) => s.name).join(', ');
            return `Error: No active skill named "${args.name}". Available skills: ${available || '(none)'}.`;
        }

        analytics.track('Agent Skill Loaded', {
            skill: skill.name,
            kind: skill.kind,
        });

        this.announceBuildStart(context);

        return skill.instructions;
    }

    /**
     * Opens the staged build display. Deliberately the earliest hook we have: the
     * planning that follows is the longest unexplained wait in the whole build.
     * Any skill counts, not just the video ones — the model often loads a how-to
     * first, and gating on kind left that opening stretch unexplained. Having no
     * video yet is what marks this as a build rather than a follow-on edit.
     *
     * Display-only, so an existing unsaved video is not worth guarding against here —
     * the stage panel is harmless on a rebuild, and create_blank_video does the real
     * "a video already exists" check. For the same reason nothing here may stop the
     * skill from loading: a panel that cannot be drawn is not worth failing the call
     * the model actually made.
     */
    private announceBuildStart(context: ToolExecutionContext): void {
        try {
            if (this.getCurrentVideoId?.() == null) {
                announceCreationStage(
                    this.dispatch,
                    this.getState,
                    context,
                    CreationStage.PLANNING,
                );
            }
        } catch {
            // no stage panel, but the skill still loads
        }
    }
}
